import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Badge } from '../components/Badge';
import { Activity, AlertTriangle, BarChart2, Briefcase, ChevronRight, Crosshair, Eye, MapPin, ShieldAlert, TrendingUp, Users, Zap } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { NASIONAL, TOP_PRIORITAS, TREN_7_HARI, statusColor } from '../data/binda';
import { useStore } from '../data/store';
import { StatusBadge } from '../components/StatusBadge';
export default function ExecutiveMode() {
  const navigate = useNavigate();
  const alertsList = useStore(s => s.alerts);
  const kpiDataList = useStore(s => s.kpi);
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  const criticalAlerts = alertsList.filter(a => a.type === 'KRITIS').slice(0, 4);
  return <div className="h-screen w-screen bg-[#0a0c10] text-foreground flex flex-col p-3 gap-3 font-sans overflow-hidden relative" data-id="element-1144">
      {/* Background Grid Pattern */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: 'linear-gradient(var(--primary) 1px, transparent 1px), linear-gradient(90deg, var(--primary) 1px, transparent 1px)',
      backgroundSize: '40px 40px'
    }} data-id="element-1145"></div>

      {/* Header */}
      <header className="flex justify-between items-center glass-panel-heavy border-b border-border/30 px-4 py-2 rounded shrink-0 z-10" data-id="element-1146">
        <div className="flex items-center gap-4" data-id="element-1147">
          <div className="w-8 h-8 bg-primary/20 border border-primary/50 rounded flex items-center justify-center shadow-[0_0_15px_rgba(var(--primary),0.3)]" data-id="element-1148">
            <Briefcase className="w-5 h-5 text-primary" data-id="element-1149" />
          </div>
          <div data-id="element-1150">
            <h1 className="text-lg font-heading font-bold tracking-widest uppercase text-primary text-glow-primary leading-tight" data-id="element-1151">
              DENS CAKRA EXECUTIVE DASHBOARD
            </h1>
            <p className="text-[9px] text-muted-foreground font-mono tracking-widest uppercase leading-tight" data-id="element-1152">
              National Strategic Situation Overview // Tingkat Rahasia
            </p>
          </div>
        </div>
        <div className="flex items-center gap-8" data-id="element-1153">
          <div className="flex flex-col items-end" data-id="element-1154">
            <div className="text-2xl font-mono font-bold tracking-wider text-[var(--status-active)] text-glow-active leading-none" data-id="element-1155">
              {time.toLocaleTimeString('id-ID', {
              hour12: false
            })}
            </div>
            <div className="text-[9px] text-muted-foreground font-mono uppercase tracking-widest mt-1" data-id="element-1156">
              {time.toLocaleDateString('id-ID', {
              weekday: 'short',
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })}
            </div>
          </div>
          <Link to="/" className="px-4 py-2 bg-background/50 border border-border/50 text-foreground rounded text-[10px] font-mono font-bold uppercase tracking-widest hover:bg-white/10 transition-colors" data-id="element-1157">
            Keluar Mode
          </Link>
        </div>
      </header>

      {/* Main Grid - 3 Columns x 2 Rows */}
      <div className="flex-1 grid grid-cols-3 grid-rows-2 gap-3 min-h-0 z-10" data-id="element-1158">
        {/* 1. National Situation Overview */}
        <Card className="glass-panel border-border/30 flex flex-col overflow-hidden" data-id="element-1159">
          <CardHeader className="p-3 border-b border-border/30 bg-black/20 shrink-0" data-id="element-1160">
            <CardTitle className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2" data-id="element-1161">
              <Eye className="w-3 h-3 text-primary" data-id="element-1162" /> Tinjauan Situasi Nasional
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 flex-1 flex flex-col justify-between" data-id="element-1163">
            <div className="grid grid-cols-2 gap-4 h-full" data-id="element-1164">
              <div className="bg-black/20 rounded border border-border/20 p-3 flex flex-col justify-center items-center text-center" data-id="element-1165">
                <Users className="w-5 h-5 text-primary mb-2 opacity-70" data-id="element-1166" />
                <p className="text-3xl font-heading font-bold text-foreground text-glow-primary" data-id="element-1167">
                  {NASIONAL.totalPersonelAktif.toLocaleString('id-ID')}
                </p>
                <p className="text-[9px] font-mono uppercase text-muted-foreground tracking-widest mt-1" data-id="element-1168">
                  Personel Aktif
                </p>
              </div>
              <div className="bg-black/20 rounded border border-border/20 p-3 flex flex-col justify-center items-center text-center" data-id="element-1169">
                <Activity className="w-5 h-5 text-[var(--status-active)] mb-2 opacity-70" data-id="element-1170" />
                <p className="text-3xl font-heading font-bold text-[var(--status-active)] text-glow-active" data-id="element-1171">
                  {NASIONAL.laporanHariIni.toLocaleString('id-ID')}
                </p>
                <p className="text-[9px] font-mono uppercase text-muted-foreground tracking-widest mt-1" data-id="element-1172">
                  Laporan Hari Ini
                </p>
              </div>
              <div className="bg-black/20 rounded border border-border/20 p-3 flex flex-col justify-center items-center text-center" data-id="element-1173">
                <ShieldAlert className="w-5 h-5 text-destructive mb-2 opacity-70" data-id="element-1174" />
                <p className="text-3xl font-heading font-bold text-destructive text-glow-destructive" data-id="element-1175">
                  {NASIONAL.wilayahKritis + NASIONAL.wilayahWaspada}
                </p>
                <p className="text-[9px] font-mono uppercase text-muted-foreground tracking-widest mt-1" data-id="element-1176">
                  Wilayah Eskalasi
                </p>
              </div>
              <div className="bg-black/20 rounded border border-border/20 p-3 flex flex-col justify-center items-center text-center" data-id="element-1177">
                <Crosshair className="w-5 h-5 text-[var(--status-standby)] mb-2 opacity-70" data-id="element-1178" />
                <p className="text-3xl font-heading font-bold text-[var(--status-standby)] text-glow-standby" data-id="element-1179">
                  {NASIONAL.laporanPrioritas}
                </p>
                <p className="text-[9px] font-mono uppercase text-muted-foreground tracking-widest mt-1" data-id="element-1180">
                  Isu Prioritas
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 2. Priority Regions */}
        <Card className="glass-panel border-border/30 flex flex-col overflow-hidden" data-id="element-1181">
          <CardHeader className="p-3 border-b border-border/30 bg-black/20 shrink-0" data-id="element-1182">
            <CardTitle className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2" data-id="element-1183">
              <MapPin className="w-3 h-3 text-primary" data-id="element-1184" /> Wilayah Prioritas
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-y-auto custom-scrollbar" data-id="element-1185">
            <div className="divide-y divide-border/20" data-id="element-1186">
              {TOP_PRIORITAS.slice(0, 5).map((region, idx) => <div key={region.kode} className="p-3 flex items-center justify-between hover:bg-white/5 cursor-pointer transition-colors" onClick={() => navigate(`/regional/${region.kode}`)} data-id="element-1187">
                  <div className="flex items-center gap-3" data-id="element-1188">
                    <div className="w-5 h-5 rounded bg-black/40 border border-border/50 flex items-center justify-center text-[10px] font-mono text-muted-foreground" data-id="element-1189">
                      {idx + 1}
                    </div>
                    <div data-id="element-1190">
                      <p className="text-sm font-bold font-heading" data-id="element-1191">
                        {region.provinsi}
                      </p>
                      <p className="text-[9px] font-mono text-muted-foreground uppercase tracking-widest" data-id="element-1192">
                        {region.laporanPrioritas} Laporan Prioritas
                      </p>
                    </div>
                  </div>
                  <StatusBadge status={region.status} className="text-[9px]" data-id="element-1193" />
                </div>)}
            </div>
          </CardContent>
        </Card>

        {/* 3. Critical Alerts */}
        <Card className="glass-panel border-border/30 flex flex-col overflow-hidden border-t-2 border-t-destructive" data-id="element-1194">
          <CardHeader className="p-3 border-b border-border/30 bg-destructive/10 shrink-0" data-id="element-1195">
            <CardTitle className="text-[10px] font-mono uppercase tracking-widest text-destructive flex items-center gap-2" data-id="element-1196">
              <AlertTriangle className="w-3 h-3" data-id="element-1197" /> Peringatan Kritis Aktif
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-y-auto custom-scrollbar" data-id="element-1198">
            <div className="divide-y divide-border/20" data-id="element-1199">
              {criticalAlerts.map(alert => <div key={alert.id} className="p-3 hover:bg-white/5 cursor-pointer transition-colors" onClick={() => navigate(`/alerts/${alert.id}`)} data-id="element-1200">
                  <div className="flex justify-between items-start mb-1" data-id="element-1201">
                    <span className="text-[10px] font-mono font-bold text-destructive bg-destructive/10 px-1.5 py-0.5 rounded border border-destructive/30" data-id="element-1202">
                      {alert.id}
                    </span>
                    <span className="text-[9px] font-mono text-muted-foreground" data-id="element-1203">
                      {alert.time}
                    </span>
                  </div>
                  <p className="text-xs font-mono text-foreground/90 line-clamp-2 mt-1" data-id="element-1204">
                    {alert.desc}
                  </p>
                  <div className="flex items-center gap-2 mt-2" data-id="element-1205">
                    <span className="text-[9px] font-mono text-muted-foreground uppercase tracking-widest" data-id="element-1206">
                      Lokasi:{' '}
                      <span className="text-foreground" data-id="element-1207">{alert.location}</span>
                    </span>
                  </div>
                </div>)}
              {criticalAlerts.length === 0 && <div className="p-6 text-center text-muted-foreground font-mono text-xs" data-id="element-1208">
                  Tidak ada peringatan kritis aktif.
                </div>}
            </div>
          </CardContent>
        </Card>

        {/* 4. KPI Summary */}
        <Card className="glass-panel border-border/30 flex flex-col overflow-hidden" data-id="element-1209">
          <CardHeader className="p-3 border-b border-border/30 bg-black/20 shrink-0" data-id="element-1210">
            <CardTitle className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2" data-id="element-1211">
              <BarChart2 className="w-3 h-3 text-primary" data-id="element-1212" /> Ringkasan KPI
              Regional
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 flex-1 min-h-0" data-id="element-1213">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1214">
              <BarChart data={kpiDataList.slice(0, 5)} layout="vertical" margin={{
              top: 5,
              right: 10,
              left: 30,
              bottom: 5
            }} data-id="element-1215">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} data-id="element-1216" />
                <XAxis type="number" domain={[0, 100]} stroke="rgba(255,255,255,0.3)" tick={{
                fontSize: 9,
                fontFamily: 'monospace'
              }} data-id="element-1217" />
                <YAxis dataKey="name" type="category" stroke="rgba(255,255,255,0.5)" tick={{
                fontSize: 9,
                fontFamily: 'monospace'
              }} width={70} data-id="element-1218" />
                <Tooltip contentStyle={{
                backgroundColor: 'rgba(10, 12, 16, 0.95)',
                borderColor: 'rgba(255,255,255,0.1)',
                borderRadius: '4px'
              }} itemStyle={{
                color: 'var(--primary)',
                fontFamily: 'monospace',
                fontSize: '10px'
              }} labelStyle={{
                color: '#fff',
                fontFamily: 'monospace',
                fontSize: '10px',
                marginBottom: '4px'
              }} data-id="element-1219" />
                <Bar dataKey="score" name="Skor KPI" fill="var(--primary)" radius={[0, 2, 2, 0]} barSize={16} data-id="element-1220" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* 5. Trend Analysis */}
        <Card className="glass-panel border-border/30 flex flex-col overflow-hidden" data-id="element-1221">
          <CardHeader className="p-3 border-b border-border/30 bg-black/20 shrink-0" data-id="element-1222">
            <CardTitle className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2" data-id="element-1223">
              <TrendingUp className="w-3 h-3 text-primary" data-id="element-1224" /> Tren Aktivitas (7
              Hari)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 flex-1 min-h-0" data-id="element-1225">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1226">
              <LineChart data={TREN_7_HARI} margin={{
              top: 5,
              right: 10,
              left: -20,
              bottom: 5
            }} data-id="element-1227">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} data-id="element-1228" />
                <XAxis dataKey="hari" stroke="rgba(255,255,255,0.3)" tick={{
                fontSize: 9,
                fontFamily: 'monospace'
              }} data-id="element-1229" />
                <YAxis stroke="rgba(255,255,255,0.3)" tick={{
                fontSize: 9,
                fontFamily: 'monospace'
              }} data-id="element-1230" />
                <Tooltip contentStyle={{
                backgroundColor: 'rgba(10, 12, 16, 0.95)',
                borderColor: 'rgba(255,255,255,0.1)',
                borderRadius: '4px'
              }} itemStyle={{
                fontFamily: 'monospace',
                fontSize: '10px'
              }} labelStyle={{
                color: '#fff',
                fontFamily: 'monospace',
                fontSize: '10px',
                marginBottom: '4px'
              }} data-id="element-1231" />
                <Line type="monotone" dataKey="laporan" name="Total" stroke="var(--status-active)" strokeWidth={2} dot={{
                r: 3,
                fill: 'var(--status-active)'
              }} data-id="element-1232" />
                <Line type="monotone" dataKey="prioritas" name="Prioritas" stroke="var(--status-standby)" strokeWidth={2} dot={{
                r: 3,
                fill: 'var(--status-standby)'
              }} data-id="element-1233" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* 6. Executive Recommendations */}
        <Card className="glass-panel border-border/30 flex flex-col overflow-hidden border-t-2 border-t-primary" data-id="element-1234">
          <CardHeader className="p-3 border-b border-border/30 bg-primary/10 shrink-0" data-id="element-1235">
            <CardTitle className="text-[10px] font-mono uppercase tracking-widest text-primary flex items-center gap-2" data-id="element-1236">
              <Zap className="w-3 h-3" data-id="element-1237" /> Rekomendasi Strategis
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 flex-1 overflow-y-auto custom-scrollbar" data-id="element-1238">
            <div className="space-y-4" data-id="element-1239">
              <div className="flex gap-3" data-id="element-1240">
                <div className="mt-0.5" data-id="element-1241">
                  <div className="w-1.5 h-1.5 rounded-full bg-destructive shadow-[0_0_5px_var(--destructive)] animate-pulse" data-id="element-1242"></div>
                </div>
                <div data-id="element-1243">
                  <p className="text-xs font-bold text-foreground mb-1" data-id="element-1244">
                    Eskalasi Pengamanan Papua
                  </p>
                  <p className="text-[10px] font-mono text-muted-foreground leading-relaxed" data-id="element-1245">
                    Terdapat peningkatan 15% pada laporan prioritas di sektor
                    timur. Disarankan penambahan personel taktis lapis kedua
                    dalam 24 jam ke depan.
                  </p>
                </div>
              </div>
              <div className="flex gap-3" data-id="element-1246">
                <div className="mt-0.5" data-id="element-1247">
                  <div className="w-1.5 h-1.5 rounded-full bg-[var(--status-standby)] shadow-[0_0_5px_var(--status-standby)]" data-id="element-1248"></div>
                </div>
                <div data-id="element-1249">
                  <p className="text-xs font-bold text-foreground mb-1" data-id="element-1250">
                    Fokus Intelijen Siber Nasional
                  </p>
                  <p className="text-[10px] font-mono text-muted-foreground leading-relaxed" data-id="element-1251">
                    Anomali trafik terdeteksi pada infrastruktur vital.
                    Alokasikan analis siber tambahan untuk memantau gateway
                    utama pemerintah.
                  </p>
                </div>
              </div>
              <div className="flex gap-3" data-id="element-1252">
                <div className="mt-0.5" data-id="element-1253">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_5px_var(--primary)]" data-id="element-1254"></div>
                </div>
                <div data-id="element-1255">
                  <p className="text-xs font-bold text-foreground mb-1" data-id="element-1256">
                    Optimalisasi KPI Jawa Barat
                  </p>
                  <p className="text-[10px] font-mono text-muted-foreground leading-relaxed" data-id="element-1257">
                    Waktu respon penanganan di Jawa Barat melambat 3 menit dari
                    rata-rata. Perlu peninjauan ulang alur komando regional.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
}